import { find } from 'lodash';
import {
    getDataForSecondMetric,
    getHotspotData, getMetricData, getXAxisData, xAxisFormatter,
} from '@domain/_screens/overview/segmented/graph/graph-data';
import { getZIndexByLayerName, LayerNames } from '@shared/constants/layers.constants';
import { ColorConstants } from '@shared/constants';
import smartViewTexts from '@shared/i18n/overview/texts';
import { PERCENTAGE_METRICS } from '@shared/constants/MetricsTypeConstants';
import { iconPath } from '@shared/components/charts/options/options.constants';

const getYAxis = (position, legend) => (
    {
        show: true,
        position,
        type: 'value',
        splitNumber: 3,
        axisTick: {
            show: false,
        },
        axisLine: {
            show: false,
            onZero: true,
        },

        axisLabel: {
            formatter: `{value}${hasPercentageSuffix(position, legend)}`,
            textStyle: {
                color: ColorConstants['light-slate-gray'],
            },
        },

        splitLine: {
            show: true,
            lineStyle: {
                color: [ColorConstants.gainsboro],
                type: 'solid',
            },
        },
    }
);

export const getSeriesBasicOptions = total => (
    {
        itemStyle: {
            normal: {
                shadowBlur: total?.length === 1 ? 1 : 3,
            },
            emphasis: {
                lineStyle: {
                    shadowBlur: 0,
                },
            },
        },
        lineStyle: {
            normal: {
                width: 0,
            },
        },
        areaStyle: {
            normal: {
                opacity: 0.5,
            },
        },
        showSymbol: true,
        smooth: true,
        symbol: 'circle',
        symbolSize: total.filter(
            element => Object.keys(element).length !== 0,
        )?.length <= 2 ? 10 : 0,
        type: 'line',
        bar: {
            label: {
                shadowColor: 'transparent',
            },
        },
    });

export const getSegmentedGraphOptions = ({
    hotSpots,
    segmentation,
    total,
    series,
    totalForComparison,
    legend,
    tooltip,
    isAccumulated,
    toggleAccumulated,
    isComparison,
}) => ({
    backgroundColor: 'transparent',
    toolbox: {
        show: true,
        showTitle: false,
        feature: {
            dataZoom: {
                title: {
                    zoom: 'Zoom',
                    back: 'One step back',
                },
            },
            myAccumulatedOption: {
                show: true,
                title: 'Accumulated data',
                icon: iconPath,
                onclick: toggleAccumulated,
            },
            dataView: {
                readOnly: true,
                title: 'Data View',
                lang: ['data view', 'close', 'refresh'],
                buttonColor: ColorConstants.salmon,
            },
            magicType: {
                type: ['line', 'bar', 'stack', 'tiled'],
                title: {
                    line: 'Line Chart',
                    bar: 'Bar Chart',
                    stack: 'Stack',
                    tiled: 'Tiled',
                },
            },
            restore: {
                title: 'Restore',
            },
            saveAsImage: {
                title: smartViewTexts.segmentedGraphFeatures.download,
            },
        },
    },
    tooltip: {
        trigger: 'axis',
        axisPointer: {
            animation: false,
        },
        position(pos, _params, el, _elRect, size) {
            const widthDif = el.getBoundingClientRect().width;
            const x = pos[0] < size.viewSize[0] * 2 / 3 ? pos[0] : pos[0] - widthDif;
            return [x, '90%'];
        },
        textStyle: {
            color: ColorConstants['regal-blue'],
            fontFamily: 'Helvetica, sans-serif',
            fontSize: '14',
        },
        iterable: true,
        triggerOn: 'mousemove|click',
        confine: false,
        extraCssText: `z-index: ${getZIndexByLayerName(LayerNames.CURR_VALUE_1)};
        background: ${ColorConstants.white} 0% 0% no-repeat padding-box;
        box-shadow: 0px 0px 10px #00000029;
        opacity: 1;`,
        ...tooltip,
    },
    axisPointer: {
        link: {
            xAxisIndex: 'all',
        },
    },
    legend: {
        icon: 'circle',
        display: true,
        selectedMode: false,
        ...legend,
    },
    xAxis: [{
        data: getXAxisData(total, segmentation, totalForComparison, isComparison),
        type: 'category',
        boundaryGap: false,
        axisTick: {
            show: false,
        },
        axisLabel: {
            textStyle: {
                color: ColorConstants['light-slate-gray'],
            },
            formatter: xAxisFormatter(segmentation),
        },
        axisLine: {
            textStyle: {
                color: ColorConstants['light-slate-gray'],
            },
            show: false,
        },
        splitLine: {
            show: true,
            lineStyle: {
                color: ColorConstants['black-squeeze'],
                type: 'solid',
            },
        },
    }],
    yAxis: [
        getYAxis(0, legend),
        getYAxis(1, legend),
    ],
    grid: {
        left: 55,
        right: 55,
    },
    series: [
        {
            data: getHotspotData(hotSpots, total, segmentation),
            xAxisIndex: 0,
            itemStyle: {
                normal: {
                    shadowBlur: 3,
                    shadowColor: ColorConstants['white-smoke'],
                    color: ColorConstants.white,
                },
                emphasis: {
                    lineStyle: {
                        shadowBlur: 0,
                    },
                },
            },
            lineStyle: {
                width: 3,
                shadowColor: 'rgba(0,0,0,0.4)',
                shadowBlur: 10,
                shadowOffsetY: 10,
            },
            showSymbol: true,
            smooth: true,
            symbol: 'circle',
            symbolSize: 12,
            type: 'line',
        },
        ...getSeries(series, isAccumulated),
    ],
});

export const getSeries = (series, isAccumulated) => {
    if (!isAccumulated) return series;
    return series.map(element => ({
        ...element,
        data: getAccumulateData(element.data),
    }));
};

const getAccumulateData = data => data.reduce((acc, current, index) => [
    ...acc,
    ((parseFloat(current) + parseFloat(acc[index - 1]))
        || parseFloat(current)).toFixed(2)],
[]);

const hasPercentageSuffix = (position, legend) => {
    const found = find(PERCENTAGE_METRICS, metric => legend.data[position] === metric.label);
    if (found) return '%';
    return '';
};


export const buildFirstAndSecondMetricSeries = ({
    firstMetric, secondMetric, total, totalForComparison, segmentation,
}) => [
    {
        color: firstMetric.metric.color,
        name: firstMetric.metric.label || '',
        data: firstMetric.isSelected && getMetricData(total, firstMetric, segmentation),
        yAxisIndex: 0,
        opacity: 0.7,
        ...getSeriesBasicOptions(total),
    },
    {
        color: secondMetric?.metric?.color,
        name: secondMetric?.metric?.label || '',
        data: secondMetric?.isSelected && getDataForSecondMetric(
            total, totalForComparison, firstMetric, secondMetric,
        ),
        opacity: 0.7,
        yAxisIndex: secondMetric?.metric?.type === 'comparison' ? 0 : 1,
        ...getSeriesBasicOptions(totalForComparison),
    }];
